import styled, { css } from "styled-components/macro";
import { Content } from "../../../components/AccordionCard/AccordionCard";

/** Shared CSS class names for product and entity profile detail pages. */
export const PROFILE_DETAIL_CLASSES = {
  groupCard: "profile-group-card",
  collectionCard: "profile-collection-card",
  assetsTable: "profile-assets-table",
  connections: "profile-connections",
  activity: "profile-activity",
  relatedEntities: "profile-related-entities",
  detailsSection: "profile-details-section",
} as const;

export type ProfileDetailClassName =
  typeof PROFILE_DETAIL_CLASSES[keyof typeof PROFILE_DETAIL_CLASSES];

const {
  groupCard,
  collectionCard,
  assetsTable,
  connections,
  activity,
  relatedEntities,
  detailsSection,
} = PROFILE_DETAIL_CLASSES;

export const profileDetailCardContainerStyles = css<{
  pageContainerSettings: string;
}>`
  div.${groupCard},
    div.${collectionCard},
    div.${assetsTable},
    div.${connections},
    div.${activity},
    div.${relatedEntities},
    div.${detailsSection} {
    background-color: #fff;
    padding: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "no_containers"
        ? 0
        : pageContainerSettings === "single_container"
        ? `${theme.resolve("spacing_2xl")} ${theme.resolve(
            "spacing_2xl"
          )} ${theme.resolve("spacing_lg")}`
        : theme.resolve("spacing_2xl")};
    border-radius: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "no_containers"
        ? 0
        : theme.resolve("radius_productDetail_card")};
    border: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "individual_containers"
        ? `${theme.resolve("border_md")} solid ${theme.resolve(
            "color_border_lighter"
          )}`
        : "none"};
    margin-bottom: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "single_container"
        ? 0
        : theme.resolve("spacing_3xl")};
  }

  div${Content} {
    border-bottom: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "individual_containers"
        ? "0"
        : `${theme.resolve("border_md")} solid ${theme.secondaryBorder}`};
    padding-bottom: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "individual_containers"
        ? "0"
        : theme.resolve("spacing_2xl")};
  }

  div.${collectionCard}, div.${connections}, div.${activity} {
    h3 {
      margin-top: 0 !important;
    }
  }
`;

export type ProfileDetailContentContainerProps = {
  pageContainerSettings: string;
  surfaceBackground: string;
  headerHeight: number;
  pageLayout: string;
  isXlScreen: boolean;
};

export const ProfileDetailContentContainer = styled.div<ProfileDetailContentContainerProps>`
  flex: 1;
  background-color: ${({ theme, pageContainerSettings, surfaceBackground }) =>
    pageContainerSettings === "no_containers" || !surfaceBackground
      ? theme.resolve("color_bg_surface_product")
      : surfaceBackground};
  [class*="TabList"] {
    padding: ${({ theme }) => theme.resolve("spacing_xs")}
      ${({ theme }) => theme.resolve("spacing_xl")} 0;
    max-width: 100%;
    width: 100%;
    z-index: 3;
    position: sticky;
    background: ${({ pageLayout, surfaceBackground }) =>
      pageLayout === "two_column"
        ? surfaceBackground
        : "var(--color_neutral_white)"};
    top: 0px;
    ${({ pageLayout, surfaceBackground }) =>
      pageLayout === "two_column" &&
      css`
        padding: ${({ theme }) => theme.resolve("spacing_xs")}
          ${({ theme }) => theme.resolve("spacing_xl")};
        background-color: ${surfaceBackground};
        border-bottom: 0 !important;
        margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
        button {
          padding: ${({ theme }) => theme.resolve("spacing_xs")}
            ${({ theme }) => theme.resolve("spacing_lg")} !important;
          border-bottom: 0;
          margin-right: ${({ theme }) => theme.resolve("spacing_xxs")};
          background-color: var(--color_neutral_white);
          z-index: 4;
          font-size: ${({ theme }) => theme.resolve("font_size_md")};
          font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
          &[data-selected] {
            background-color: ${({ theme }) =>
              theme.resolve("color_brand_primary")};
            color: ${({ theme }) => theme.resolve("color_neutral_white")};
            font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
          }
          &:first-child {
            margin-left: ${({ theme }) => theme.resolve("spacing_xs")};
          }
          &:last-child {
            margin-right: ${({ theme }) => theme.resolve("spacing_xs")};
          }
        }
        &::before {
          content: "";
          position: absolute;
          bottom: 0;
          left: ${({ theme }) => theme.resolve("spacing_xl")};
          top: 0;
          right: ${({ theme }) => theme.resolve("spacing_xl")};
          width: calc(
            100% - ${({ theme }) => theme.resolve("spacing_4xl")} - 2px
          );
          background: var(--color_neutral_white);
          z-index: 1;
          border: ${({ theme }) => theme.resolve("border_md")} solid
            ${({ theme }) => theme.resolve("color_border_lighter")};
        }
      `}
  }
  div[data-reach-tabs] {
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }
  div[data-reach-tab-panels] {
    flex: 1;
    padding: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "no_containers"
        ? 0
        : `${theme.resolve("spacing_3xl")} ${theme.resolve("spacing_xl")}`};
  }
  div[data-reach-tab-panel] {
    padding: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "no_containers"
        ? `${theme.resolve("spacing_3xl")} ${theme.resolve("spacing_xl")}`
        : 0};
    border-radius: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "single_container"
        ? theme.resolve("radius_productDetail_card")
        : 0};
    border: ${({ theme, pageContainerSettings }) =>
      pageContainerSettings === "single_container"
        ? `${theme.resolve("border_md")} solid ${theme.resolve(
            "color_border_lighter"
          )}`
        : "none"};
    background-color: ${({ pageContainerSettings }) =>
      pageContainerSettings === "single_container" ? "#fff" : "none"};
  }

  ${profileDetailCardContainerStyles}
`;
